import api from './api';

export interface SugerenciaCompra {
  id: string;
  ingrediente_id: number;
  ingrediente_nombre: string;
  cantidad_actual: number;
  cantidad_necesaria: number;
  cantidad_sugerida: number;
  proveedor_id?: number;
  proveedor_nombre?: string;
  precio_unitario?: number;
  fecha_necesaria: string;
  prioridad: 'alta' | 'media' | 'baja';
  estado: 'pendiente' | 'en_proceso' | 'completada';
  observaciones?: string;
  unidad_medida: string;
}

export interface GenerarSugerenciasRequest {
  dias_adelante?: number;
  porcentaje_extra?: number;
  solo_faltantes?: boolean;
}

interface ListarSugerenciasResponse {
  data: SugerenciaCompra[];
  total: number;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface SugerenciasBackendResponse {
  resumen: {
    periodo: {
      fecha_inicio: string;
      fecha_fin: string;
      dias: number;
    };
    configuracion: {
      porcentaje_extra: number;
      incluir_stock: boolean;
    };
    estadisticas: {
      total_pedidos: number;
      total_ingredientes: number;
      total_proveedores: number;
      costo_total_estimado: number;
    };
  };
  sugerencias_por_proveedor: Array<{
    proveedor: string;
    total_estimado: number;
    ingredientes: Array<{
      ingrediente_id: number;
      nombre: string;
      categoria: string;
      unidad_medida: string;
      stock_actual: number;
      stock_minimo: number;
      cantidad_necesaria: number;
      cantidad_sugerida: number;
      costo_unitario: number;
      costo_total: number;
      proveedor: string | null;
    }>;
  }>;
  detalle_ingredientes: any[];
}

export const sugerenciasCompraAPI = {
  // Nuevo endpoint basado en pedidos futuros (RECOMENDADO)
  generarDesdePedidos: async (dias: number = 2): Promise<ApiResponse<any>> => {
    const response = await api.get(
      `/sugerencias-compra/pedidos-futuros?dias=${dias}`
    );

    const data = response.data.data || response.data;

    // Transformar del formato nuevo al formato esperado por el frontend
    const sugerenciasPorProveedor: Record<
      string,
      {
        ingredientes: any[];
        total_estimado: number;
        proveedor_id?: number | null;
      }
    > = {};

    // Agrupar ingredientes por proveedor
    if (data.ingredientes && Array.isArray(data.ingredientes)) {
      data.ingredientes.forEach((ing: any) => {
        const proveedor = ing.proveedor_principal || 'Sin proveedor';
        const proveedorId = ing.proveedor_principal_id;

        if (!sugerenciasPorProveedor[proveedor]) {
          sugerenciasPorProveedor[proveedor] = {
            ingredientes: [],
            total_estimado: 0,
            proveedor_id: proveedorId, // ✅ Agregamos el ID del proveedor
          };
        }

        sugerenciasPorProveedor[proveedor].ingredientes.push({
          ingrediente_id: ing.ingrediente_id,
          nombre_ingrediente: ing.ingrediente_nombre,
          categoria: ing.categoria || 'General',
          cantidad_necesaria: ing.cantidad_necesaria,
          stock_actual: ing.stock_actual,
          cantidad_en_transito: ing.cantidad_en_transito || 0,
          cantidad_sugerida: ing.cantidad_a_comprar,
          unidad: ing.unidad_medida,
          proveedor_principal: ing.proveedor_principal,
          proveedor_principal_id: proveedorId,
          costo_unitario: ing.costo_unitario,
          costo_estimado: ing.costo_total_estimado,
          prioridad:
            ing.prioridad === 'urgente'
              ? 'alta'
              : ing.prioridad === 'media'
                ? 'media'
                : 'baja',
          dias_stock_restante: 0,
          recetas_afectadas: ing.recetas_afectadas,
          dias_afectados: ing.dias_afectados,
          // Fase 6: unidad base y formato de compra preferido
          cantidad_necesaria_base: ing.cantidad_necesaria_base,
          cantidad_sugerida_base: ing.cantidad_a_comprar_base,
          stock_actual_base: ing.stock_actual_base,
          unidad_base_simbolo: ing.unidad_base_simbolo,
          unidad_display_simbolo: ing.unidad_display_simbolo,
          tipo_fisico: ing.tipo_fisico,
          formato_compra_sugerido: ing.formato_compra_sugerido,
          cantidad_packs_sugerida: ing.cantidad_packs_sugerida,
          unidades_por_pack: ing.unidades_por_pack,
          unidad_pack_simbolo: ing.unidad_pack_simbolo,
          // 'demanda' = hay pedidos del período que lo necesitan
          // 'reposicion' = no hay demanda pero stock <= mínimo
          motivo: ing.motivo,
          stock_minimo: ing.stock_minimo,
        });

        sugerenciasPorProveedor[proveedor].total_estimado +=
          ing.costo_total_estimado;
      });
    }

    const transformedData = {
      fecha_calculo: data.fecha_calculo,
      parametros: {
        dias_adelante: dias,
        porcentaje_extra: 0,
        incluir_stock: true,
      },
      sugerencias_por_proveedor: sugerenciasPorProveedor,
      resumen: data.resumen,
      detalle_ingredientes: data.ingredientes || [],
    };

    return {
      success: true,
      data: transformedData,
    };
  },

  generar: async (
    params?: GenerarSugerenciasRequest
  ): Promise<ApiResponse<SugerenciasBackendResponse>> => {
    // El backend usa GET /sugerencias-compra con query parameters
    const queryParams = new URLSearchParams();
    if (params?.dias_adelante !== undefined)
      queryParams.append('diasAdelante', params.dias_adelante.toString());
    if (params?.porcentaje_extra !== undefined)
      queryParams.append('porcentajeExtra', params.porcentaje_extra.toString());
    if (params?.solo_faltantes !== undefined)
      queryParams.append('incluirStock', (!params.solo_faltantes).toString());

    const response = await api.get(
      `/sugerencias-compra?${queryParams.toString()}`
    );
    return response.data;
  },

  listar: async (params?: any): Promise<ListarSugerenciasResponse> => {
    const response = await api.get('/sugerencias-compra', { params });
    return response.data;
  },

  obtenerPorId: async (id: string): Promise<ApiResponse<SugerenciaCompra>> => {
    const response = await api.get(`/sugerencias-compra/${id}`);
    return response.data;
  },

  actualizar: async (
    id: string,
    datos: Partial<SugerenciaCompra>
  ): Promise<ApiResponse<SugerenciaCompra>> => {
    const response = await api.put(`/sugerencias-compra/${id}`, datos);
    return response.data;
  },

  eliminar: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/sugerencias-compra/${id}`);
    return response.data;
  },

  convertirAOrdenes: async (
    sugerencias_ids: string[]
  ): Promise<ApiResponse<any>> => {
    const response = await api.post('/sugerencias-compra/convertir-a-ordenes', {
      sugerencias_ids,
    });
    return response.data;
  },
};
